import { proccessNotes } from './components/JoinedForm/Helpers'

export const templateFormToProductKeys = new Set(['height', 'length', 'width', 'weight', 'shippingWeight', 'tags'])

export const numericFields = new Set(['height', 'length', 'width', 'weight', 'shippingWeight'])

// Only match the first set of numbers, remove everything else
// Make sure the value is positive
export const removeNonNumericCharacters = str => {
  if (typeof str !== 'string') return str

  // eslint-disable-next-line no-useless-escape
  let replacedStr = str.replace(/([^\.0-9])(.*)/g, '')

  replacedStr = parseFloat(replacedStr) >= 0 ? parseFloat(replacedStr).toString() : undefined
  return replacedStr
}

const mergeTemplateValuesIntoProduct = (product, productAttrsFromTemplate) => {
  const mergedProduct = { ...product }
  for (const key in productAttrsFromTemplate) {
    if (productAttrsFromTemplate[key] || productAttrsFromTemplate[key] === 0) {
      let newValue = productAttrsFromTemplate[key]

      if (numericFields.has(key)) {
        newValue = removeNonNumericCharacters(newValue)
      }

      // Don't clobber existing tags for draft products
      if (key === 'tags' && mergedProduct.tags?.length > 0) {
        newValue = [...product.tags, ...newValue]
      }

      // Automatically populate the shippingWeight field when the weight field is set
      if (key === 'weight' && !Object.prototype.hasOwnProperty.call(productAttrsFromTemplate, 'shippingWeight')) {
        mergedProduct.shippingWeight = newValue
      }

      mergedProduct[key] = newValue
    }
  }

  return mergedProduct
}

const serializeTag = field => `${field.variableName}_${field.props.value}`

export const getProductAttrsFromTemplateForm = form => {
  const jsForm = form.toJS()
  const productAttributesFromForm: any = {}
  for (const key in jsForm) {
    if (jsForm[key]) {
      const field = jsForm[key]
      const fieldValue = field.props.value
      const productKey = jsForm[key].productField
      if (templateFormToProductKeys.has(productKey) && field.props) {
        if (productKey === 'tags') {
          productAttributesFromForm.tags = productAttributesFromForm.tags || []
          if (fieldValue) productAttributesFromForm.tags.push(serializeTag(field))
        } else {
          productAttributesFromForm[productKey] = fieldValue
        }
      }
    }
  }
  return productAttributesFromForm
}

export const templateProductFieldMerge = (form, product) => {
  if (!form) return product

  const productAttrsFromTemplate = getProductAttrsFromTemplateForm(form)

  return mergeTemplateValuesIntoProduct(product, productAttrsFromTemplate)
}

export interface ProductForTemplateUtils extends Record<string, unknown> {
  length: number
  width: number
  height: number
  weight: number
  shippingWeight: string
  tags: string[]
}

export const setDraftProductValuesInTemplateForm = (product: ProductForTemplateUtils, template) => {
  if (!template) return template
  if (!template.get('form')) return template

  const form = template.get('form')
  const newForm = form.map(field => {
    const productKey = field.get('productField')

    if (
      templateFormToProductKeys.has(productKey) &&
      field.get('props') &&
      product[productKey] &&
      productKey !== 'tags' // don't set tags as default template value because multiple template fields can map to it
    ) {
      // toString() avoids cases like product height, where it might begin as a number, then change into a string once the user touches the field.
      return field.setIn(['props', 'value'], product[productKey]?.toString())
    }
    return field
  })
  return template.set('form', newForm)
}
const hammoqValueToBool = str => str === 'yes' || str === 'Yes' || str === 'YES'

export const setNotesValuesInTemplateForm = (notes, template) => {
  if (!template || !notes || notes.length === 0) return template
  const attributes = proccessNotes(notes, true)
  if (Object.keys(attributes).length < 1) return template

  let newTemplate = template
  newTemplate.get('form').forEach((input, index) => {
    const inputType = input.get('type')
    const propsEle = input.get('props')
    const variableName = input.get('variableName')
    const labelName = propsEle.get('label')

    let autofillValue = null
    // this works to correclty insert the value for Multiple Choice fields, but the UI element doesn't appear correctly checked, so we will not support it
    // if (inputType === 'checkboxGroup') {
    //   propsEle.get('options').forEach(optionValue => {
    //     if (
    //       optionValue.get('key').toLowerCase() == attributes[variableName.toLowerCase()]?.toLowerCase() ||
    //       optionValue.get('key').toLowerCase() == attributes[labelName.toLowerCase()]?.toLowerCase()
    //     ) {
    //       autofillValue = [optionValue.get('key')]
    //       // console.log(autofillValue) // leaving this here in case yall want to see what it looks like, this was copied based on what occurs in the checkbox is selecting, inpsecting the onRendererChange function below
    //     }
    //   })
    // } else
    if (inputType === 'checkbox') {
      if (attributes[variableName.toLowerCase()] || attributes[labelName.toLowerCase()])
        autofillValue = hammoqValueToBool(attributes[variableName.toLowerCase()] || attributes[labelName.toLowerCase()])
    } else if (inputType === 'combobox' || inputType === 'select' || inputType === 'radio') {
      if (attributes[variableName.toLowerCase()] || attributes[labelName.toLowerCase()]) {
        propsEle?.get('options')?.forEach(optionValue => {
          if (
            optionValue?.get('key').toLowerCase() === attributes[variableName.toLowerCase()]?.toLowerCase() ||
            optionValue?.get('key').toLowerCase() === attributes[labelName.toLowerCase()]?.toLowerCase()
          ) {
            autofillValue = optionValue.get('key')
          }
        })
      }
    }
    // ignore these two form types until the bug(https://app.asana.com/0/1204859485810598/1206288730596279/f) is resolved and we can get the block above commented out to work correctly,
    // heading will never be supported since it isn't an input
    else if (inputType !== 'checkboxGroup' || inputType !== 'heading') {
      autofillValue = attributes[variableName.toLowerCase()] || attributes[labelName.toLowerCase()]
    }

    if (autofillValue) {
      newTemplate = newTemplate.setIn(['form', index, 'props', 'value'], autofillValue)
    }
  })
  return newTemplate
}

export function clearUprightPerformanceMarks() {
  performance.getEntriesByType('mark').forEach(m => {
    if (m.name.includes('upright:')) {
      performance.clearMarks(m.name)
    }
  })
}

export function newVariantFromProduct(product, title) {
  return {
    id: null,
    title,
    sku: '',
    quantity: 1,
    inventoryLocationId: product.inventoryLocationId
  }
}

export function validVariantPricing(variant) {
  if (
    variant.compareAtPrice === null ||
    variant.compareAtPrice === undefined ||
    variant.compareAtPrice === '' ||
    +variant.compareAtPrice >= +variant.price
  ) {
    return true
  }

  return false
}
